import React, { useMemo, useEffect, useRef, useState } from "react";
import { useHierarchicalAnalytics } from "../components/Analytics/hooks/useHierarchicalAnalytics";
import HierarchicalChart from "../components/Analytics/components/HierarchicalChart";
import FilterPanel from "../components/Analytics/components/FilterPanel";
import { KpiCard } from "../components/Analytics/components/KpiCard";
import { AttendanceHeatmap } from "../components/Analytics/components/AttendanceHeatmap";
import { useHierarchicalFilters } from "../components/Analytics/hooks/useHierarchicalFilters";
import Button from "../components/UI/Button";
import WorkedHoursChart from "../components/Analytics/components/WorkedHoursChart";
import ChartWrapper from "../components/UI/ChartWrapper";

const HierarchicalAnalyticsPage = () => {
    const {
        dateRange,
        limit,
        viewMode,
        sortBy,
        handleFilterFieldChange,
        goToDepartments,
        goToShifts,
        goToEmployees,
        resetToCompany,
        currentLevel,
        currentIds,
    } = useHierarchicalFilters();

    // Параметры для API
    const groupBy = useMemo(() => {
        if (currentLevel === "employee") return "employee";
        if (currentLevel === "shift") return "shift";
        if (currentLevel === "department") return "department";
        return "company";
    }, [currentLevel]);

    const apiParams = useMemo(() => {
        return {
            groupBy,
            departmentIds: currentIds.departmentId
                ? [currentIds.departmentId]
                : [],
            shiftIds: currentIds.shiftId ? [currentIds.shiftId] : [],
            userIds: [],
            dateRange,
            limit,
            sortOrder: viewMode === "bottom" ? "asc" : "desc",
            sortBy,
            viewMode,
        };
    }, [currentIds, dateRange, limit, viewMode, sortBy, groupBy]);

    const {
        data,
        chartData,
        workedHoursChartData,
        isLoading,
        error,
        heatmapChartData,
        breadcrumbData,
    } = useHierarchicalAnalytics(apiParams);

    // Переход вниз по иерархии по клику
    const handleChartElementClick = (index) => {
        const item = data[index];
        if (currentLevel === "company") goToDepartments();
        else if (currentLevel === "department") goToShifts(item.departmentId);
        else if (currentLevel === "shift")
            goToEmployees(item.departmentId, item.shiftId);
    };

    // Переход "Назад"
    const handleGoBack = () => {
        if (currentLevel === "employee") goToShifts(currentIds.departmentId);
        else if (currentLevel === "shift") goToDepartments();
        else if (currentLevel === "department") resetToCompany();
    };

    // Заголовки
    const chartTitle = useMemo(() => {
        if (currentLevel === "employee") return "Статистика по сотрудникам";
        if (currentLevel === "shift") return "Статистика по сменам";
        if (currentLevel === "department") return "Статистика по отделам";
        return "Общая статистика по предприятию";
    }, [currentLevel]);

    const heatmapTitle = useMemo(() => {
        if (currentLevel === "employee")
            return "Посещаемость сотрудников по дням";
        if (currentLevel === "shift") return "Посещаемость по сменам и дням";
        if (currentLevel === "department")
            return "Посещаемость по отделам и дням";
        return "Общая посещаемость по дням";
    }, [currentLevel]);

    // KPI карточки
    const kpiCards = useMemo(() => {
        if (!data || data.length === 0) return [];
        const totalShifts =
            data.reduce((sum, item) => sum + item.totalShifts, 0) || 1;
        return [
            {
                title: "Опоздания",
                value: data.reduce((sum, item) => sum + item.delayCount, 0),
                percent: Math.round(
                    (data.reduce((sum, item) => sum + item.delayCount, 0) /
                        totalShifts) *
                        100
                ),
                status: "delay",
                icon: "clock",
            },
            {
                title: "Переработки",
                value: data.reduce((sum, item) => sum + item.overtimeCount, 0),
                percent: Math.round(
                    (data.reduce((sum, item) => sum + item.overtimeCount, 0) /
                        totalShifts) *
                        100
                ),
                status: "overtime",
                icon: "overtime",
            },
            {
                title: "Ранние уходы",
                value: data.reduce((sum, item) => sum + item.earlyExitCount, 0),
                percent: Math.round(
                    (data.reduce((sum, item) => sum + item.earlyExitCount, 0) /
                        totalShifts) *
                        100
                ),
                status: "earlyExit",
                icon: "exit",
            },
            {
                title: "Пропуски",
                value: data.reduce((sum, item) => sum + item.missedCount, 0),
                percent: Math.round(
                    (data.reduce((sum, item) => sum + item.missedCount, 0) /
                        totalShifts) *
                        100
                ),
                status: "missed",
                icon: "close",
            },
        ];
    }, [data]);

    const getRowLabel = (item) => {
        if (currentLevel === "employee")
            return item.userName || "Неизвестный сотрудник";
        if (currentLevel === "shift")
            return item.shiftName || "Неизвестная смена";
        if (currentLevel === "department")
            return item.departmentName || "Неизвестный отдел";
        return "Предприятие";
    };

    const renderBreadcrumbs = () => (
        <div className="flex items-center ml-4 text-lg text-gray-400">
            {breadcrumbData.map((crumb, idx) => (
                <span key={crumb.id}>
                    {idx > 0 && <span className="mx-1">{">"}</span>}
                    {crumb.label}
                </span>
            ))}
        </div>
    );

    if (error) {
        return (
            <div className="container mx-auto p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    <p>Ошибка при загрузке данных: {error.message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl text-gray-300 font-bold mb-6">
                Аналитика посещаемости
            </h1>
            <div>
                {kpiCards.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                        {kpiCards.map((kpi, index) => (
                            <KpiCard key={index} {...kpi} />
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="col-start-1 -col-end-1 bg-[#2D2D30] rounded-lg shadow p-4">
                        <h2 className="text-lg text-gray-300 font-semibold mb-4">
                            {heatmapTitle}
                        </h2>
                        <ChartWrapper isLoading={isLoading} height="250px">
                            <AttendanceHeatmap
                                data={heatmapChartData}
                                sortBy={
                                    sortBy === "totalViolations"
                                        ? "totalCount"
                                        : sortBy
                                }
                                viewMode={viewMode}
                                limit={limit}
                                height="250px"
                            />
                        </ChartWrapper>
                    </div>
                    <div className="bg-[#2D2D30] rounded-lg shadow p-4">
                        <h2 className="text-lg text-gray-300 font-semibold mb-2">
                            {chartTitle}
                        </h2>
                        <ChartWrapper isLoading={isLoading} height="300px">
                            <HierarchicalChart
                                data={chartData}
                                height="300px"
                            />
                        </ChartWrapper>
                    </div>
                    <div className="bg-[#2D2D30] rounded-lg shadow p-4">
                        <ChartWrapper isLoading={isLoading} height="300px">
                            <h2 className="text-lg text-gray-300 font-semibold mb-2">
                                {chartTitle}
                            </h2>
                            <WorkedHoursChart
                                data={workedHoursChartData}
                                title="Отработанные часы"
                                height="300px"
                            />
                        </ChartWrapper>
                    </div>
                </div>
                <FilterPanel
                    dateRange={dateRange}
                    viewMode={viewMode}
                    limit={limit}
                    sortBy={sortBy}
                    onFieldChange={handleFilterFieldChange}
                />
                {data && data.length > 0 ? (
                    <div
                        style={{ opacity: isLoading ? 0 : 1 }}
                        className="bg-[#2D2D30] rounded-lg border border-[#3E3E42] shadow overflow-hidden mb-6 transition-opacity duration-500"
                    >
                        {currentLevel !== "company" && (
                            <div className="mb-4 flex justify-between items-center">
                                {renderBreadcrumbs()}
                                <Button
                                    onClick={handleGoBack}
                                    variant="secondary"
                                    className="float-right m-2"
                                    type="button"
                                >
                                    ← Назад
                                </Button>
                            </div>
                        )}
                        <table className="min-w-full divide-y divide-gray-400">
                            <thead className="bg-[#1E1E1E]">
                                <tr>
                                    <th className="px-6 py-3 text-gray-400">
                                        Название
                                    </th>
                                    <th className="px-6 py-3 text-gray-400">
                                        Всего смен
                                    </th>
                                    <th className="px-6 py-3 text-gray-400">
                                        Опоздания
                                    </th>
                                    <th className="px-6 py-3 text-gray-400">
                                        Переработки
                                    </th>
                                    <th className="px-6 py-3 text-gray-400">
                                        Ранние уходы
                                    </th>
                                    <th className="px-6 py-3 text-gray-400">
                                        Пропуски
                                    </th>
                                    <th className="px-6 py-3 text-gray-400">
                                        Общий индекс
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-[#2D2D30] text-gray-400 divide-y divide-gray-400">
                                {data.map((item, index) => (
                                    <tr
                                        key={index}
                                        onClick={() =>
                                            handleChartElementClick(index)
                                        }
                                        className="hover:bg-[#3E3E42] cursor-pointer"
                                    >
                                        <td className="px-6 py-4">
                                            {getRowLabel(item)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.totalShifts}
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.delayCount} (
                                            {item.totalShifts > 0
                                                ? Math.round((item.delayCount / item.totalShifts) * 100)
                                                : 0}
                                            %)
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.overtimeCount} (
                                            {item.totalShifts > 0
                                                ? Math.round((item.overtimeCount / item.totalShifts) * 100)
                                                : 0}
                                            %)
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.earlyExitCount} (
                                            {item.totalShifts > 0
                                                ? Math.round((item.earlyExitCount / item.totalShifts) * 100)
                                                : 0}
                                            %)
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.missedCount} (
                                            {item.totalShifts > 0
                                                ? Math.round((item.missedCount / item.totalShifts) * 100)
                                                : 0}
                                            %)
                                        </td>
                                        <td className="px-6 py-4">
                                            {(item.delayCount || 0) +
                                                (item.missedCount || 0) +
                                                (item.earlyExitCount || 0)}{" "}
                                            нарушений
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="bg-[#2D2D30] p-4 rounded text-center">
                        <p className="text-gray-400">
                            Нет данных для отображения. Попробуйте изменить
                            фильтры.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HierarchicalAnalyticsPage;
